import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import type { i18n as I18nInstance } from 'i18next';
import { initI18n, type SupportedLanguage } from '../../shared/i18n/initI18n';

interface LanguageContextValue {
  language: SupportedLanguage;
  setLanguage: (lng: SupportedLanguage) => void;
  i18n: I18nInstance | null;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

interface I18nProviderProps {
  children: ReactNode;
}

export function I18nProvider({ children }: I18nProviderProps) {
  const [i18n, setI18n] = useState<I18nInstance | null>(null);
  const [language, setLanguageState] = useState<SupportedLanguage>('en');

  useEffect(() => {
    let mounted = true;
    let cleanup: (() => void) | undefined;

    void initI18n().then((instance) => {
      if (!mounted) {
        return;
      }
      setI18n(instance);
      setLanguageState((instance.language as SupportedLanguage) ?? 'en');

      const handler = (lng: string) => {
        setLanguageState((lng as SupportedLanguage) ?? 'en');
      };
      instance.on('languageChanged', handler);
      cleanup = () => {
        instance.off('languageChanged', handler);
      };
    });

    return () => {
      mounted = false;
      cleanup?.();
    };
  }, []);

  const setLanguage = useCallback(
    (lng: SupportedLanguage) => {
      if (!i18n) return;
      void i18n.changeLanguage(lng);
    },
    [i18n]
  );

  const contextValue = useMemo<LanguageContextValue>(
    () => ({ language, setLanguage, i18n }),
    [language, setLanguage, i18n]
  );

  if (!i18n) {
    return null;
  }

  return (
    <I18nextProvider i18n={i18n} defaultNS="translation">
      <LanguageContext.Provider value={contextValue}>{children}</LanguageContext.Provider>
    </I18nextProvider>
  );
}

export function useLanguageContext(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguageContext must be used within I18nProvider');
  }

  return context;
}

export function LanguageSwitcher() {
  const { language, setLanguage } = useLanguageContext();
  const { t } = useTranslation();
  const nextLanguage: SupportedLanguage = language === 'en' ? 'zh' : 'en';
  const label = t('app.actions.changeLanguage', {
    language: t(`app.languageNames.${nextLanguage}`)
  });

  return (
    <button
      type="button"
      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-ai-surface-glow/30 bg-ai-surface-elevated/70 text-sm text-ai-text-secondary transition-colors hover:border-ai-primary/60 hover:text-ai-text-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
      onClick={() => setLanguage(nextLanguage)}
      aria-label={label}
      title={label}
    >
      <svg
        className="h-4 w-4"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        role="presentation"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M2 12h20" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10" />
      </svg>
    </button>
  );
}
